import React, { useMemo } from 'react';
import { User, Mail, Phone, Download, ArrowLeft } from 'lucide-react';

const PlayersReport = ({ players, onBack, clubName }) => {
  // Sort players alphabetically and format names
  const formattedPlayers = useMemo(() => {
    return (players || [])
      .map(player => {
        // Handle name splitting safely
        const fullName = player.name || '';
        const nameParts = fullName.trim().split(' ');
        
        let firstName = '';
        let lastName = '';
        
        if (nameParts.length > 0) {
          firstName = nameParts[0];
          // Join the rest of the name parts as the last name
          if (nameParts.length > 1) {
            lastName = nameParts.slice(1).join(' ');
          }
        }
        
        return {
          ...player,
          firstName,
          lastName,
          email: player.email || '-',
          phone: player.phone || player.phoneNumber || '-'
        };
      })
      .sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : '';
        const nameB = b.name ? b.name.toLowerCase() : '';
        return nameA.localeCompare(nameB);
      });
  }, [players]);

  const exportToCSV = () => {
    let csvContent = `Players Report - ${clubName || 'Club'}\n\n`;
    csvContent += `First Name,Last Name,Email,Phone\n`;
    
    formattedPlayers.forEach(player => {
      csvContent += `"${player.firstName}","${player.lastName}","${player.email}","${player.phone}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `players-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Reports Hub
        </button>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
          <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
            <User size={20} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Players Directory</h3>
            <p className="text-sm text-gray-500">Contact information for {formattedPlayers.length} active players</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 text-gray-600 text-sm">
                <th className="px-6 py-4 font-semibold border-b">First Name</th>
                <th className="px-6 py-4 font-semibold border-b">Last Name</th>
                <th className="px-6 py-4 font-semibold border-b">Email</th>
                <th className="px-6 py-4 font-semibold border-b">Phone</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {formattedPlayers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    No players found
                  </td>
                </tr>
              ) : (
                formattedPlayers.map((player) => (
                  <tr key={player.id || player.email} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      {player.firstName}
                    </td>
                    <td className="px-6 py-3 text-gray-900">
                      {player.lastName}
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-gray-400" />
                        {player.email}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-gray-400" />
                        {player.phone}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayersReport;
